import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Action, State, StateContext } from '@ngxs/store';
import { MessageService } from 'primeng/api';
import { catchError, EMPTY, tap } from 'rxjs';

import { getToastContentBySeverity } from '@customer-portal/shared/helpers/custom-toast';
import { ToastSeverity } from '@customer-portal/shared/models';

import {
  QUALTRICS_INVALID_COMMENT_ERROR,
  QUALTRICS_SURVEY_TYPES,
} from '../helpers/qualtrics-feedback.constants';
import {
  QualtricsSubmitResponse,
  QualtricsSurvey,
  QualtricsSurveyDefinition,
} from '../models';
import { QualtricsSurveyFeedbackService } from '../services/data-services/qualtrics/qualtrics-survey-feedback.service';
import { QualtricsSurveyMapperService } from '../services/mappers/qualtrics-survey.mapper.service';
import {
  LoadQualtricsSurveyDetails,
  LoadQualtricsSurveyDetailsError,
  LoadQualtricsSurveyDetailsSuccess,
  SubmitQualtricsSurvey,
  SubmitQualtricsSurveyError,
  SubmitQualtricsSurveySuccess,
} from './actions/qualtrics-survey-feedback.actions';

export interface QualtricsSurveyEntry {
  survey: QualtricsSurvey | null;
  surveyDefinition: QualtricsSurveyDefinition | null;
  isLoaded: boolean;
  error: string;
  submitError: string | null;
  submitValidationError: string | null;
  submitSuccess: QualtricsSubmitResponse | null;
}

export interface QualtricsSurveyFeedbackStateModel {
  surveys: Record<string, QualtricsSurveyEntry>;
}

const defaultSurveyEntry: QualtricsSurveyEntry = {
  survey: null,
  surveyDefinition: null,
  isLoaded: false,
  error: '',
  submitError: null,
  submitValidationError: null,
  submitSuccess: null,
};

const defaultState: QualtricsSurveyFeedbackStateModel = {
  surveys: {},
};

@State<QualtricsSurveyFeedbackStateModel>({
  name: 'qualtricsFeedback',
  defaults: defaultState,
})
@Injectable()
export class QualtricsSurveyFeedbackState {
  private readonly qualtricsService = inject(QualtricsSurveyFeedbackService);
  private readonly messageService = inject(MessageService);
  private readonly ts = inject(TranslocoService);

  @Action(LoadQualtricsSurveyDetails)
  loadSurveyDetails(
    ctx: StateContext<QualtricsSurveyFeedbackStateModel>,
    action: LoadQualtricsSurveyDetails,
  ) {
    const surveyType = action.surveyType || QUALTRICS_SURVEY_TYPES.ALWAYS_ON;

    this.patchSurveyEntry(ctx, surveyType, { isLoaded: false, error: '' });

    return this.qualtricsService.getQualtricsSurveyDetails().pipe(
      tap((response: any) => {
        if (!response?.isSuccess) {
          throw new Error(
            response?.message || 'Failed to load Qualtrics survey',
          );
        }

        const responseData = response?.data;

        if (!Array.isArray(responseData) || responseData.length === 0) {
          ctx.dispatch(new LoadQualtricsSurveyDetailsSuccess(surveyType, null));

          return;
        }

        const survey = responseData[0];
        const normalizedQuestions =
          QualtricsSurveyMapperService.normalizeSurveyQuestions(survey);

        const transformedData = {
          surveyId: survey?.id ?? survey?.surveyId ?? 0,
          surveyName: survey?.surveyName ?? '',
          surveyType: survey?.surveyType ?? '',
          qualtricsSurveyId: survey?.qualtricsSurveyId ?? '',
          versionNumber: survey?.versionNumber ?? 0,
          surveyDetails: {
            Questions: normalizedQuestions,
          },
        } as unknown as QualtricsSurvey;

        ctx.dispatch(
          new LoadQualtricsSurveyDetailsSuccess(surveyType, transformedData),
        );
      }),
      catchError((error: Error) => {
        ctx.dispatch(
          new LoadQualtricsSurveyDetailsError(
            surveyType,
            error.message || 'Failed to load Qualtrics survey',
          ),
        );

        return EMPTY;
      }),
    );
  }

  @Action(LoadQualtricsSurveyDetailsSuccess)
  loadSurveyDetailsSuccess(
    ctx: StateContext<QualtricsSurveyFeedbackStateModel>,
    action: LoadQualtricsSurveyDetailsSuccess,
  ) {
    this.patchSurveyEntry(ctx, action.surveyType, {
      survey: action.survey,
      surveyDefinition: QualtricsSurveyMapperService.mapToSurveyDefinition(
        action.survey,
      ),
      isLoaded: true,
      error: '',
    });
  }

  @Action(LoadQualtricsSurveyDetailsError)
  loadSurveyDetailsError(
    ctx: StateContext<QualtricsSurveyFeedbackStateModel>,
    action: LoadQualtricsSurveyDetailsError,
  ) {
    this.patchSurveyEntry(ctx, action.surveyType, {
      survey: null,
      surveyDefinition: null,
      isLoaded: true,
      error: action?.error || 'Failed to load Qualtrics survey',
    });
  }

  @Action(SubmitQualtricsSurvey)
  submitQualtricsSurvey(
    ctx: StateContext<QualtricsSurveyFeedbackStateModel>,
    action: SubmitQualtricsSurvey,
  ) {
    this.patchSurveyEntry(ctx, action.surveyType, {
      submitError: null,
      submitValidationError: null,
      submitSuccess: null,
    });

    return this.qualtricsService
      .onSubmitQualtricsSurveyFeedback(action.payload as any)
      .pipe(
        tap((response) => {
          ctx.dispatch(
            new SubmitQualtricsSurveySuccess(action.surveyType, response),
          );
        }),
        catchError((error: any) => {
          const errorMessage = String(
            error.networkError?.error?.message ??
              error.networkError?.error ??
              error.error?.message ??
              error.error ??
              error.message ??
              'Failed to submit Qualtrics survey',
          );

          ctx.dispatch(
            new SubmitQualtricsSurveyError(action.surveyType, errorMessage),
          );

          return EMPTY;
        }),
      );
  }

  @Action(SubmitQualtricsSurveySuccess)
  submitQualtricsSurveySuccess(
    ctx: StateContext<QualtricsSurveyFeedbackStateModel>,
    action: SubmitQualtricsSurveySuccess,
  ) {
    this.patchSurveyEntry(ctx, action.surveyType, {
      submitError: null,
      submitValidationError: null,
      submitSuccess: action.response,
    });
  }

  @Action(SubmitQualtricsSurveyError)
  submitQualtricsSurveyError(
    ctx: StateContext<QualtricsSurveyFeedbackStateModel>,
    action: SubmitQualtricsSurveyError,
  ) {
    if (action.errorMessage.includes(QUALTRICS_INVALID_COMMENT_ERROR)) {
      this.patchSurveyEntry(ctx, action.surveyType, {
        submitValidationError: action.errorMessage,
      });

      const message = getToastContentBySeverity(ToastSeverity.Error);

      message.summary = this.ts.translate('qualtrics.feedbackSubmissionError');

      this.messageService.add(message);
    } else {
      this.patchSurveyEntry(ctx, action.surveyType, {
        submitError: action.errorMessage,
        submitValidationError: null,
        submitSuccess: null,
      });
    }
  }

  private patchSurveyEntry(
    ctx: StateContext<QualtricsSurveyFeedbackStateModel>,
    surveyType: string,
    entry: Partial<QualtricsSurveyEntry>,
  ): void {
    const state = ctx.getState();
    const existingEntry = state.surveys[surveyType] ?? defaultSurveyEntry;

    ctx.patchState({
      surveys: {
        ...state.surveys,
        [surveyType]: { ...existingEntry, ...entry },
      },
    });
  }
}
